import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Img, { ImgProps } from "@insite/mobius/Img";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import parse from "html-react-parser";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    richContent = "richContent",
    imageUrl = "imageUrl",
    altText = "altText",
    imageLink = "imageLink",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.richContent]: string;
        [fields.imageUrl]: string;
        [fields.altText]: string;
        [fields.imageLink]: LinkFieldValue;
        [fields.customCSS]: string;
    };
}

export interface FormImageContainerStyles {
    wrapper?: InjectableCss;
    gridContainer?: GridContainerProps;
    leftColumn?: GridItemProps;
    rightColumn?: GridItemProps;
    richContentWrapper?: InjectableCss;
    zoneWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    img?: ImgProps;
    imageLink?: LinkPresentationProps;
}

export const formImageContainerStyles: FormImageContainerStyles = {
    gridContainer: {
        isFullWidth: true,
        gap: 0, // remove gutters inside the full-bleed area
    },
    leftColumn: {
        width: [12, 12, 12, 7, 7],
        css: css`
            background: ${getColor("common.accent")};
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
            padding: 2rem;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        order: 0;
                        align-items: center;
                    `, // xs
                    css`
                        order: 0;
                        align-items: center;
                    `, // sm
                    css`
                        order: 0;
                        align-items: center;
                    `, // md
                    css`
                        order: 0;
                        align-items: stretch;
                        padding: 6rem clamp(5rem, 5vw, 10rem) 2rem 5rem;
                    `, // lg
                    css`
                        order: 0;
                        align-items: stretch;
                        padding: 6rem clamp(5rem, 5vw, 12rem) 2rem 5rem;
                    `, // xl
                ])}
        `,
    },
    rightColumn: {
        width: [12, 12, 12, 5, 5],
        css: css`
            background: ${getColor("primary.main")};
            display: flex;
            align-items: center;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 2rem;
                        justify-content: center;
                    `, // xs
                    css`
                        padding: 2rem;
                        justify-content: center;
                    `, // sm
                    css`
                        padding: 2rem;
                        justify-content: center;
                    `, // md
                    css`
                        padding: 2rem 0 2rem 2rem;
                        justify-content: flex-start;
                    `, // lg
                    css`
                        padding: 2rem 0 2rem 3rem;
                        justify-content: flex-start;
                    `, // xl
                ])}
        `,
    },
    richContentWrapper: {
        css: css`
            margin-bottom: 1rem;
            max-width: 720px;
            width: 100%;
            margin-inline: auto;
        `,
    },
    zoneWrapper: {
        css: css`
            padding: 0;
            max-width: 720px;
            width: 100%;
            margin-inline: auto;
        `,
    },
    imageWrapper: {
        css: css`
            width: auto;
            max-width: none;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        max-width: 560px;
                        margin: 0 auto;
                        transform: none;
                    `, // xs
                    css`
                        width: 100%;
                        max-width: 600px;
                        margin: 0 auto;
                        transform: none;
                    `, // sm
                    css`
                        width: 100%;
                        max-width: 640px;
                        margin: 0 auto;
                        transform: none;
                    `, // md
                    css`
                        width: clamp(560px, 52vw, 860px);
                        transform: translateX(-12%);
                    `, // lg
                    css`
                        width: clamp(600px, 54vw, 900px);
                        transform: translateX(-14%);
                    `, // xl
                ])}
        `,
    },

    img: {
        css: css`
            width: 100%;
            height: auto;
            display: block;
        `,
    },
};

const FormImageContainer: FC<OwnProps> = ({ id, fields }) => {
    const styles = useMergeStyles("image", formImageContainerStyles);

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    const image = fields.imageUrl ? (
        fields.imageLink?.value ? (
            <Link href={fields.imageLink.value} {...styles.imageLink}>
                <Img src={fields.imageUrl} altText={fields.altText || "Form Image"} loading="lazy" {...styles.img} />
            </Link>
        ) : (
            <Img src={fields.imageUrl} altText={fields.altText || "Form Image"} loading="lazy" {...styles.img} />
        )
    ) : null;

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="form-image-container" {...styles.wrapper}>
                <GridContainer {...styles.gridContainer}>
                    <GridItem {...styles.leftColumn}>
                        <StyledWrapper className="rich-content-wrapper" {...styles.richContentWrapper}>
                            {fields.richContent && parse(fields.richContent, parserOptions)}
                        </StyledWrapper>
                        <StyledWrapper className="zone-wrapper" {...styles.zoneWrapper}>
                            <Zone contentId={id} zoneName="FormZone" />
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.rightColumn}>
                        <StyledWrapper className="image-wrapper" {...styles.imageWrapper}>
                            {image}
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.form-image-container {
}

.rich-content-wrapper {
}

.zone-wrapper {
}

.image-wrapper {
}`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: FormImageContainer,
    definition: {
        group: "Basic",
        icon: "layout",
        displayName: "Form Image Container",
        fieldDefinitions: [
            {
                name: fields.richContent,
                displayName: "Rich Content",
                editorTemplate: "RichTextField",
                defaultValue:
                    "<h2>EASY AS 1-2-CLICK</h2><p>1. Fill out the form below with a few details about what you're looking for.<br/>2. We'll review your request and follow up if we need more info.<br/>3. Sit tight! Your sample will ship out faster than a forklift at break time.</p>",
                fieldType: "Contextual",
                tooltip: "Rich content for the left column",
                tab: basicTab,
            },
            {
                name: fields.imageUrl,
                displayName: "Image",
                fieldType: "Contextual",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tooltip: "Image for the right column",
                tab: basicTab,
            },
            {
                name: fields.altText,
                displayName: "Alt Text",
                fieldType: "Contextual",
                editorTemplate: "TextField",
                defaultValue: "",
                tooltip: "Alternative text for the image",
                tab: basicTab,
            },
            {
                name: fields.imageLink,
                displayName: "Image Link",
                fieldType: "Contextual",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tooltip: "Optional link for the image",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
